/**
 * Training & Workshop (T&W) API Service
 * All calls route through /api/crm/tw (auth-protected) or /api/crm/public/tw (public)
 */

import api from './api';

const TW = '/crm/tw';
const TW_PUBLIC = '/crm/public/tw';

// ============================================================================
// LOOKUP (minimal-data dropdowns for forms)
// ============================================================================
export const lookupVenues = (params = {}) => api.get(`${TW}/lookup/venues`, { params });
export const lookupFacilitators = (params = {}) => api.get(`${TW}/lookup/facilitators`, { params });
export const lookupWorkshops = (params = {}) => api.get(`${TW}/lookup/workshops`, { params });
export const lookupSchedules = (params = {}) => api.get(`${TW}/lookup/schedules`, { params });
export const lookupParticipants = (params = {}) => api.get(`${TW}/lookup/participants`, { params });
export const lookupParticipantById = (id) => api.get(`${TW}/lookup/participants/${id}`);
export const lookupWorkshopById = (id) => api.get(`${TW}/lookup/workshops/${id}`);
export const lookupAllocations = (params = {}) => api.get(`${TW}/lookup/allocations`, { params });
export const lookupPricingPreview = (params = {}) => api.get(`${TW}/lookup/pricing-preview`, { params });

// ============================================================================
// DASHBOARD
// ============================================================================
export const getTWDashboard = () => api.get(`${TW}/dashboard`);

// ============================================================================
// CALENDAR
// ============================================================================
export const getTWCalendarEvents = (params = {}) => api.get(`${TW}/calendar`, { params });

// ============================================================================
// VENUES
// ============================================================================
export const getVenues = (params = {}) => api.get(`${TW}/venues`, { params });
export const getVenueById = (id) => api.get(`${TW}/venues/${id}`);
export const createVenue = (data) => api.post(`${TW}/venues`, data);
export const updateVenue = (id, data) => api.put(`${TW}/venues/${id}`, data);
export const deleteVenue = (id, data) => api.delete(`${TW}/venues/${id}`, { data });

// ============================================================================
// FACILITATORS
// ============================================================================
export const getFacilitators = (params = {}) => api.get(`${TW}/facilitators`, { params });
export const getFacilitatorById = (id) => api.get(`${TW}/facilitators/${id}`);
export const createFacilitator = (data) => api.post(`${TW}/facilitators`, data);
export const updateFacilitator = (id, data) => api.put(`${TW}/facilitators/${id}`, data);
export const deleteFacilitator = (id, data) => api.delete(`${TW}/facilitators/${id}`, { data });
export const addFacilitatorRating = (id, data) => api.post(`${TW}/facilitators/${id}/ratings`, data);

// ============================================================================
// PARTICIPANTS
// ============================================================================
export const getParticipants = (params = {}) => api.get(`${TW}/participants`, { params });
export const getParticipantById = (id) => api.get(`${TW}/participants/${id}`);
export const createParticipant = (data) => api.post(`${TW}/participants`, data);
export const updateParticipant = (id, data) => api.put(`${TW}/participants/${id}`, data);
export const deleteParticipant = (id, data) => api.delete(`${TW}/participants/${id}`, { data });
export const getParticipantAllocations = (id, params = {}) => api.get(`${TW}/participants/${id}/allocations`, { params });
export const getParticipantInvoices = (id, params = {}) => api.get(`${TW}/participants/${id}/invoices`, { params });
export const getParticipantCreditBalance = (id) => api.get(`${TW}/participants/${id}/credit-balance`);

// ============================================================================
// WORKSHOPS
// ============================================================================
export const getWorkshops = (params = {}) => api.get(`${TW}/workshops`, { params });
export const getWorkshopById = (id) => api.get(`${TW}/workshops/${id}`);
export const calculateWorkshopPricing = (id, params = {}) => api.get(`${TW}/workshops/${id}/pricing`, { params });
export const createWorkshop = (data) => api.post(`${TW}/workshops`, data);
export const updateWorkshop = (id, data) => api.put(`${TW}/workshops/${id}`, data);
export const updateWorkshopAccountMappings = (id, accountMappings) =>
  api.patch(`${TW}/workshops/${id}/account-mappings`, { accountMappings });

export const getTwFinanceDefaults = () => api.get(`${TW}/finance-defaults`);
export const updateTwFinanceDefaults = (accountMappings, bankReceivingAccountId) =>
  api.put(`${TW}/finance-defaults`, {
    accountMappings,
    ...(bankReceivingAccountId !== undefined ? { bankReceivingAccountId } : {}),
  });
export const deleteWorkshop = (id, data) => api.delete(`${TW}/workshops/${id}`, { data });
export const publishWorkshop = (id, data = {}) => api.post(`${TW}/workshops/${id}/publish`, data);
export const approveWorkshop = (id, data = {}) => api.post(`${TW}/workshops/${id}/approve`, data);
export const cancelWorkshop = (id, data = {}) => api.post(`${TW}/workshops/${id}/cancel`, data);

// ============================================================================
// SCHEDULES
// ============================================================================
export const getSchedules = (params = {}) => api.get(`${TW}/schedules`, { params });
export const getScheduleById = (id) => api.get(`${TW}/schedules/${id}`);
export const createSchedule = (data) => api.post(`${TW}/schedules`, data);
export const updateSchedule = (id, data) => api.put(`${TW}/schedules/${id}`, data);
export const deleteSchedule = (id, data) => api.delete(`${TW}/schedules/${id}`, { data });
export const cancelSchedule = (id, data) => api.post(`${TW}/schedules/${id}/cancel`, data);
export const convertScheduleToLeads = (id, data) => api.post(`${TW}/schedules/${id}/convert-to-leads`, data);
export const getScheduleConversionStatus = (id) => api.get(`${TW}/schedules/${id}/conversion-status`);

// ============================================================================
// ALLOCATIONS
// ============================================================================
export const getAllocations = (params = {}) => api.get(`${TW}/allocations`, { params });
export const getAllocationById = (id) => api.get(`${TW}/allocations/${id}`);
export const createAllocation = (data) => api.post(`${TW}/allocations`, data);
export const updateAllocation = (id, data) => api.put(`${TW}/allocations/${id}`, data);
export const deleteAllocation = (id, data) => api.delete(`${TW}/allocations/${id}`, { data });
export const confirmAllocation = (id, data = {}) => api.post(`${TW}/allocations/${id}/confirm`, data);
export const cancelAllocation = (id, data) => api.post(`${TW}/allocations/${id}/cancel`, data);
export const requestReallocation = (id, data) => api.post(`${TW}/allocations/${id}/reallocate`, data);
export const checkinAllocation = (id, data) => api.post(`${TW}/allocations/${id}/checkin`, data);
export const sendAllocationPaymentLink = (id, data = {}) => api.post(`${TW}/allocations/${id}/send-payment-link`, data);
export const issueCertificate = (id, data = {}) => api.post(`${TW}/allocations/${id}/issue-certificate`, data);

// ============================================================================
// QUESTIONNAIRES
// ============================================================================
export const getQuestionnaires = (params = {}) => api.get(`${TW}/questionnaires`, { params });
export const getQuestionnaireById = (id) => api.get(`${TW}/questionnaires/${id}`);
export const createQuestionnaire = (data) => api.post(`${TW}/questionnaires`, data);
export const updateQuestionnaire = (id, data) => api.put(`${TW}/questionnaires/${id}`, data);
export const deleteQuestionnaire = (id) => api.delete(`${TW}/questionnaires/${id}`);
export const publishQuestionnaire = (id) => api.post(`${TW}/questionnaires/${id}/publish`);
export const getQuestionnaireResponses = (id, params = {}) => api.get(`${TW}/questionnaires/${id}/responses`, { params });

// ============================================================================
// INVOICES
// ============================================================================
export const getInvoices = (params = {}) => api.get(`${TW}/invoices`, { params });
export const getInvoiceById = (id) => api.get(`${TW}/invoices/${id}`);
export const createInvoice = (data) => api.post(`${TW}/invoices`, data);
export const updateInvoice = (id, data) => api.put(`${TW}/invoices/${id}`, data);
export const deleteInvoice = (id, data) => api.delete(`${TW}/invoices/${id}`, { data });
export const submitInvoiceForApproval = (id, data = {}) => api.post(`${TW}/invoices/${id}/submit-for-approval`, data);
export const approveInvoice = (id, data = {}) => api.post(`${TW}/invoices/${id}/approve`, data);
export const cancelInvoice = (id, data = {}) => api.post(`${TW}/invoices/${id}/cancel`, data);
export const voidInvoice = (id, data = {}) => api.post(`${TW}/invoices/${id}/void`, data);
export const sendInvoice = (id, data = {}) => api.post(`${TW}/invoices/${id}/send`, data);
export const downloadInvoicePdf = (id) => api.get(`${TW}/invoices/${id}/pdf`, { responseType: 'blob' });
export const createCreditNote = (id, data) => api.post(`${TW}/invoices/${id}/credit-note`, data);
export const createDebitNote = (id, data) => api.post(`${TW}/invoices/${id}/debit-note`, data);

// ============================================================================
// RECEIPTS
// ============================================================================
export const getReceipts = (params = {}) => api.get(`${TW}/receipts`, { params });
export const getReceiptById = (id) => api.get(`${TW}/receipts/${id}`);
export const cancelReceipt = (id, data) => api.post(`${TW}/receipts/${id}/cancel`, data);
export const reverseReceipt = (id, data) => api.post(`${TW}/receipts/${id}/reverse`, data);
export const emailReceipt = (id, data = {}) => api.post(`${TW}/receipts/${id}/email`, data);
export const printReceipt = (id, { variant } = {}) =>
  api.get(`${TW}/receipts/${id}/print`, {
    responseType: 'blob',
    params: variant ? { variant } : undefined,
  });

// ============================================================================
// PAYMENTS
// ============================================================================
export const getPayments = (params = {}) => api.get(`${TW}/payments`, { params });
export const getPaymentById = (id) => api.get(`${TW}/payments/${id}`);
export const createPayment = (data) => api.post(`${TW}/payments`, data);
export const verifyPayment = (id, data = {}) => api.post(`${TW}/payments/${id}/verify`, data);
export const approvePayment = (id, body = {}) => api.post(`${TW}/payments/${id}/approve`, body);
export const rejectPayment = (id, data) => api.post(`${TW}/payments/${id}/reject`, data);
export const reversePayment = (id, data) => api.post(`${TW}/payments/${id}/reverse`, data);
export const reopenRejectedPayment = (id, data = {}) => api.post(`${TW}/payments/${id}/reopen-rejected`, data);

// ============================================================================
// APPROVALS
// ============================================================================
export const getApprovals = (params = {}) => api.get(`${TW}/approvals`, { params });
export const getApprovalById = (id) => api.get(`${TW}/approvals/${id}`);
export const approveRequest = (id, data = {}) => api.post(`${TW}/approvals/${id}/approve`, data);
export const rejectRequest = (id, data) => api.post(`${TW}/approvals/${id}/reject`, data);
export const delegateRequest = (id, data) => api.post(`${TW}/approvals/${id}/delegate`, data);
export const cancelApprovalRequest = (id, data = {}) => api.post(`${TW}/approvals/${id}/cancel`, data);

// ============================================================================
// AUDIT TRAIL (who created / updated / deleted / cancelled / major actions)
// ============================================================================
const AUDIT_PATHS = {
  venue: 'venues',
  facilitator: 'facilitators',
  participant: 'participants',
  workshop: 'workshops',
  schedule: 'schedules',
  allocation: 'allocations',
  invoice: 'invoices',
  receipt: 'receipts',
  payment: 'payments',
};

export const getTwAuditLog = (entity, id, params = {}) =>
  api.get(`${TW}/${AUDIT_PATHS[entity] || entity}/${id}/audit`, { params });

// ============================================================================
// REPORTS
// ============================================================================
export const getTWAllocationReport = (params = {}) => api.get(`${TW}/reports/allocation`, { params });
export const getTWPaymentDetailReport = (params = {}) => api.get(`${TW}/reports/payment-detail`, { params });
export const getTWDebtorReport = (params = {}) => api.get(`${TW}/reports/debtors`, { params });
export const getTWRevenueReport = (params = {}) => api.get(`${TW}/reports/revenue`, { params });
export const getTWFacilitatorReport = (params = {}) => api.get(`${TW}/reports/facilitator`, { params });
export const getTWAttendanceReport = (params = {}) => api.get(`${TW}/reports/attendance`, { params });
export const getTWTaxReport = (params = {}) => api.get(`${TW}/reports/tax`, { params });
export const getTWWorkshopInvoicedListingReport = (params = {}) => api.get(`${TW}/reports/workshop-invoiced-listing`, { params });
export const getTWCertificateReport = (params = {}) => api.get(`${TW}/reports/certificates`, { params });

// ============================================================================
// PUBLIC ENDPOINTS (no auth token required)
// ============================================================================
export const getPublicPaymentDetails = (token) => api.get(`${TW_PUBLIC}/payment/${token}`, { skipAuth: true });
export const initiatePublicPaycorpPayment = (token, data) => api.post(`${TW_PUBLIC}/payment/${token}/paycorp/initiate`, data, { skipAuth: true });
export const submitPublicManualPayment = (token, data) => api.post(`${TW_PUBLIC}/payment/${token}/submit`, data, { skipAuth: true });
export const getPublicQuestionnaire = (token) => api.get(`${TW_PUBLIC}/questionnaire/${token}`, { skipAuth: true });
export const submitPublicQuestionnaireResponse = (token, data) => api.post(`${TW_PUBLIC}/questionnaire/${token}`, data, { skipAuth: true });
